import { Button, Input, Modal, notify } from '@affine/component';
import type { Loss } from '@affine/core/blocksuite/docx-export/types';
import { exportDocToBlob } from '@affine/core/components/hooks/affine/use-export-page';
import type { GoogleConnectFailure } from '@affine/core/components/hooks/use-google-connect';
import {
  useGoogleConnectionStatus,
  useGoogleConnectPopup,
} from '@affine/core/components/hooks/use-google-connect';
import { FetchService } from '@affine/core/modules/cloud';
import { DocsService } from '@affine/core/modules/doc';
import {
  HtmlAdapterFactoryIdentifier,
  MarkdownAdapterFactoryIdentifier,
} from '@blocksuite/affine/shared/adapters';
import type { BlockStdScope } from '@blocksuite/affine/std';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parseRecipients, RecipientsInput } from './recipients';
import * as styles from './styles.css';

// Markdown, HTML and Word are producible today (Word via YOD-428). PDF stays
// in the list (so it's visible as a destination, not a secret) but is
// disabled: `printToPdf` drives the browser's native print dialog and hands
// back no bytes at all, so there is nothing to attach.
type Format = 'markdown' | 'html' | 'docx' | 'pdf';
type ProducibleFormat = Exclude<Format, 'pdf'>;
// The subset that shares the adapter-config shape `exportDocToBlob` expects.
// `docx` is produced by a different function (`exportDocToDocxBlob`) with a
// different signature, so it is deliberately excluded from this map and
// branched on separately below.
type AdapterProducibleFormat = Exclude<ProducibleFormat, 'docx'>;

const FORMAT_CONFIG: Record<
  AdapterProducibleFormat,
  {
    identifier: typeof HtmlAdapterFactoryIdentifier;
    fileExtension: string;
    contentType: string;
    indexFileName: string;
  }
> = {
  markdown: {
    identifier: MarkdownAdapterFactoryIdentifier,
    fileExtension: '.md',
    contentType: 'text/plain',
    indexFileName: 'index.md',
  },
  html: {
    identifier: HtmlAdapterFactoryIdentifier,
    fileExtension: '.html',
    contentType: 'text/html',
    indexFileName: 'index.html',
  },
};

/**
 * Exported for testing: the count drives a singular/plural message, and the
 * warning is suppressed entirely when nothing was lost. Non-docx exports
 * always pass `[]` here (see the export effect below), so this also
 * naturally never fires for Markdown/HTML.
 */
export function lossWarning(
  losses: Loss[]
): { count: number; plural: boolean } | null {
  if (!losses.length) return null;
  return { count: losses.length, plural: losses.length !== 1 };
}

// "Remember the last choice for the session" — a module-level variable
// rather than persisted storage: it survives the dialog closing and
// reopening within the same tab, and resets on reload, which is all the
// requirement asks for.
let lastFormatForSession: ProducibleFormat = 'markdown';

/**
 * Test-only escape hatch. `lastFormatForSession` is module-level *by design*
 * (see above) so it outlives a single render — which also means it outlives
 * a single test in the same file. `dialog.spec.tsx` resets it in an
 * `afterEach` so one test switching format can never leak the default into
 * a later, unrelated test. Nothing in production ever calls this.
 */
export function __resetLastFormatForSessionForTesting(): void {
  lastFormatForSession = 'markdown';
}

/**
 * Discriminates a `POST /api/google/send` failure body into what the dialog
 * should show.
 *
 * MUST read `body.name`, never `body.code`. `UserFriendlyError.toJSON()`
 * (backend/src/base/error/def.ts) emits `code` as the HTTP *reason phrase*
 * ("Forbidden") for every `action_forbidden`-type error, so a revoked grant
 * (`GOOGLE_RECONNECT_REQUIRED`) and a never-connected account
 * (`GOOGLE_NOT_CONNECTED`) serialise with the identical `code`. Only `name`
 * tells them apart. Collapsing them shows a user whose grant was revoked a
 * "Connect" button that reads as already-done and leads nowhere.
 */
export function mapSendError(body: {
  name?: string;
}): 'connect' | 'reconnect' | 'failed' {
  switch (body.name) {
    case 'GOOGLE_NOT_CONNECTED':
      return 'connect';
    case 'GOOGLE_RECONNECT_REQUIRED':
      return 'reconnect';
    default:
      return 'failed';
  }
}

/**
 * The name of the file the recipient will actually receive. `exportDocToBlob`
 * silently zips when the doc has assets, so a dialog that always echoes the
 * format the user picked (e.g. "Markdown") can send a `.zip` while claiming
 * otherwise. This exists as an explicit seam so the dialog always renders
 * the real filename instead of reconstructing one from the chosen format.
 */
export function attachmentLabel({ filename }: { filename: string }): string {
  return filename;
}

type Attachment = { blob: Blob; filename: string };

/**
 * What the dialog believes about the Google connection right now. Seeded by
 * `GET /api/google/status` when the dialog opens — so a never-connected user
 * sees Connect immediately instead of discovering it by way of a red error
 * banner after their first send — and overridden by a send that comes back
 * `GOOGLE_NOT_CONNECTED` / `GOOGLE_RECONNECT_REQUIRED`, since a grant can be
 * revoked between opening this dialog and pressing Send.
 */
type ConnectionState =
  | 'loading'
  | 'connected'
  | 'not-connected'
  | 'reconnect-required'
  /** The status call itself failed — see `canSend` for why we still allow a send. */
  | 'unknown';

const SPINNER_DELAY_MS = 1000;

/**
 * The largest RAW attachment the backend will accept, mirrored from
 * `plugins/google-workspace/constants.ts` (`GMAIL_MAX_ATTACHMENT_RAW_BYTES`):
 * Gmail's 25MB ceiling applies to the whole encoded MIME message, so the raw
 * bound backs out base64's 4/3 inflation, the 78/76 line-wrap overhead from
 * RFC 2045, and a fixed slack for the rest of the envelope.
 *
 * The backend stays authoritative — this is a pre-flight check, not a second
 * copy of the rule. It exists because the real oversize path is otherwise
 * *unreachable as advice*: multer's `LIMIT_FILE_SIZE` surfaces as Nest's
 * `PayloadTooLargeException`, and `mapAnyError` (base/nestjs/exception.ts)
 * only special-cases `http-errors`' `HttpError`, so it falls through to
 * `InternalServerError`. The user would get "Please try again" — advice that
 * can never work — and Railway would log a 500 that reads as a server bug.
 * Checking here means the one genuinely useful suggestion (Markdown) is the
 * one the user actually sees.
 */
const GMAIL_MAX_ENCODED_BYTES = 25 * 1024 * 1024;
const ENVELOPE_SLACK_BYTES = 256 * 1024;
const BASE64_INFLATION = 4 / 3;
const LINE_WRAP_OVERHEAD = 78 / 76;
export const MAX_ATTACHMENT_RAW_BYTES = Math.floor(
  (GMAIL_MAX_ENCODED_BYTES - ENVELOPE_SLACK_BYTES) /
    (BASE64_INFLATION * LINE_WRAP_OVERHEAD)
);

const TOO_LARGE_MESSAGE =
  'This attachment is too large to send. Try Markdown — it is dramatically smaller than an HTML export with assets.';

export interface SendEmailDialogProps {
  docId: string;
  open: boolean;
  onClose: () => void;
}

export const SendEmailDialog = ({
  docId,
  open,
  onClose,
}: SendEmailDialogProps) => {
  const fetchService = useService(FetchService);
  const docsService = useService(DocsService);

  const docRecord = useLiveData(docsService.list.doc$(docId));
  const docTitle = useLiveData(docRecord?.title$);

  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [format, setFormat] = useState<ProducibleFormat>(lastFormatForSession);

  const [attachment, setAttachment] = useState<Attachment | null>(null);
  // Blocks the docx converter had to drop (e.g. an unsupported embed).
  // Always `[]` for Markdown/HTML — see the export effect below.
  const [losses, setLosses] = useState<Loss[]>([]);
  // Three states, not a boolean: a *failed* export is not the same as a
  // finished one with nothing to attach. Conflating them made the status line
  // claim "No attachment — document is empty" about a document that had
  // simply failed to export, and let the user send an empty-handed email
  // believing that was intended.
  const [exportState, setExportState] = useState<'running' | 'done' | 'failed'>(
    'running'
  );

  const [sending, setSending] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Asked once per opening, and again after every popup round trip. Not while
  // the dialog is closed: `enabled` gates it so a closed dialog never polls.
  const { status: fetchedStatus, refresh: refreshStatus } =
    useGoogleConnectionStatus({ enabled: open });
  // A send that came back GOOGLE_NOT_CONNECTED / GOOGLE_RECONNECT_REQUIRED
  // is newer information than the status read at open time, so it wins until
  // the next successful re-check.
  const [connectionOverride, setConnectionOverride] = useState<
    'not-connected' | 'reconnect-required' | null
  >(null);
  const connection: ConnectionState =
    connectionOverride ??
    (fetchedStatus === 'error' ? 'unknown' : fetchedStatus);

  // The dialog owns the OAuth popup itself. It has to: the Share-menu item
  // that opens this dialog is inside a menu that unmounts the moment it is
  // selected, so nothing up there survives long enough to run a 120s connect
  // flow. Keeping it here is also the whole point of using a popup instead
  // of a redirect — the compose fields (recipients/subject/body/format) are
  // never unmounted, so they are still exactly as the user left them when
  // the popup comes back.
  const handleConnected = useCallback(() => {
    setError(null);
    // Re-check rather than assuming: the popup reports that the OAuth dance
    // finished, not that the grant we now hold is usable. Dropping the
    // override first means a stale "not connected" can't outlive the fix.
    setConnectionOverride(null);
    refreshStatus().catch(console.error);
  }, [refreshStatus]);

  const handleConnectFailed = useCallback((failure: GoogleConnectFailure) => {
    notify.error({
      title: 'Failed to connect Google account',
      message:
        failure.reason === 'timeout'
          ? 'The connection window took too long to respond. If it is still open, please close it and try again.'
          : failure.reason === 'popup-error' && failure.error instanceof Error
            ? failure.error.message
            : 'Please try again',
    });
  }, []);

  const {
    connect: handleGoogleAuthRequired,
    connecting: googleAuthPending,
    showSpinner: showGoogleAuthSpinner,
  } = useGoogleConnectPopup({
    onConnected: handleConnected,
    onFailed: handleConnectFailed,
  });

  // Reset the form only on the transition into `open`, not on every render
  // while it stays open — a failed send must leave everything the user typed
  // intact (see `handleSend`'s catch branch), and `docTitle` can itself
  // change while the dialog is sitting open (e.g. sync catching up).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRecipients('');
      setSubject(docTitle || 'Untitled');
      setBody('');
      setFormat(lastFormatForSession);
      setError(null);
      setConnectionOverride(null);
    }
    wasOpenRef.current = open;
  }, [open, docTitle]);

  // After choosing a format, export the doc and show the real filename the
  // recipient will get — including the `.zip` the export silently forces
  // when the doc has assets.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAttachment(null);
    setLosses([]);
    setExportState('running');

    (async () => {
      // Mirrors the lookup in use-export-page.ts's exportHandler: there is
      // exactly one mounted editor for the doc currently being shared.
      const editorHost = document.querySelector('editor-host') as
        | (Element & { std?: BlockStdScope })
        | null;
      const std = editorHost?.std;
      // The store MUST be the editor's own (`std.store`, see
      // BlockStdScope in blocksuite/framework/std/src/scope/std-scope.ts) —
      // the mounted, loaded document, exactly as use-export-page.ts uses
      // `editor.doc.blockSuiteDoc`. Re-deriving one from the collection
      // (`docCollection.getDoc(docId)?.getStore({ id: docId })`) hands the
      // adapter an unloaded store with no blocks: `exportDocToBlob` returns
      // null and the dialog truthfully reports "document is empty" about a
      // document full of content.
      const store = std?.store;
      if (!std || !store) {
        // Not "empty" — we could not read the document at all. Reporting
        // this as a successful export with no attachment is a lie the user
        // acts on.
        if (!cancelled) setExportState('failed');
        return;
      }
      // The dialog is opened for `docId`, but `querySelector` returns the
      // FIRST mounted editor-host. If those ever disagree we would attach a
      // different document than the one the user believes they are sending,
      // and email it to third parties — silently. Never attach on mismatch.
      if (store.id !== docId) {
        console.error(
          'Refusing to export for email: mounted editor is showing a different document',
          { expected: docId, mounted: store.id }
        );
        if (!cancelled) setExportState('failed');
        return;
      }
      if (format === 'docx') {
        // Dynamic: this is the lazy boundary for the whole docx module
        // graph, including the `docx` library itself — a static import here
        // would put it in the bundle for every user who never exports Word.
        const { exportDocToDocxBlob } = await import(
          '@affine/core/blocksuite/docx-export'
        );
        const result = await exportDocToDocxBlob(store, std);
        if (!cancelled) {
          setAttachment({ blob: result.blob, filename: result.filename });
          setLosses(result.losses);
          setExportState('done');
        }
        return;
      }

      const result = await exportDocToBlob(store, std, FORMAT_CONFIG[format]);
      if (!cancelled) {
        setAttachment(result);
        setLosses([]);
        setExportState('done');
      }
    })().catch(err => {
      if (!cancelled) {
        setExportState('failed');
        console.error('Failed to export document for email', err);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, format, docId]);

  const handleFormatChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value as Format;
      if (next === 'pdf') return; // disabled option; guard against a forced change
      setFormat(next);
      lastFormatForSession = next;
    },
    []
  );

  const handleSend = useCallback(() => {
    let addresses: string[];
    try {
      addresses = parseRecipients(recipients);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid recipients');
      return;
    }
    if (addresses.length === 0) {
      setError('Add at least one recipient');
      return;
    }
    if (attachment && attachment.blob.size > MAX_ATTACHMENT_RAW_BYTES) {
      setError(TOO_LARGE_MESSAGE);
      return;
    }

    setSending(true);
    setError(null);
    const spinnerTimer = setTimeout(
      () => setShowSpinner(true),
      SPINNER_DELAY_MS
    );

    const formData = new FormData();
    formData.set('to', addresses.join(', '));
    formData.set('subject', subject);
    formData.set('body', body);
    if (attachment) {
      // Wrap in a `File` rather than relying on `FormData#set`'s third
      // (filename) argument: some `FormData` implementations only honour a
      // File's own `.name` and silently ignore the append-time hint, which
      // would submit an unnamed "blob" attachment. Wrapping here is correct
      // everywhere the hint would have been.
      const file = new File([attachment.blob], attachment.filename, {
        type: attachment.blob.type,
      });
      formData.set('file', file);
    }

    fetchService
      .fetch('/api/google/send', { method: 'POST', body: formData })
      .then(res => res.json())
      .then((data: { messageId: string }) => {
        notify.success({
          title: 'Email sent',
          message: `Message ID: ${data.messageId}`,
        });
        onClose();
      })
      .catch((err: unknown) => {
        // A failed send is NEVER retryable — no retry button, no automatic
        // retry. The dialog stays open with everything the user typed
        // intact so they can fix a bad address and deliberately send again,
        // which is a different act from retrying the same send.
        const body = (err ?? {}) as {
          name?: string;
          status?: number;
          message?: string;
        };
        if (body.status === 413) {
          // Backstop for a rejection on Gmail's own side. The likelier
          // oversize path — the backend's multer limit — never reaches the
          // network now; see MAX_ATTACHMENT_RAW_BYTES above.
          setError(TOO_LARGE_MESSAGE);
          return;
        }
        const kind = mapSendError(body);
        if (kind === 'failed') {
          setError(body.message || 'Failed to send email. Please try again.');
        } else {
          // Newer than whatever the status check said when the dialog
          // opened: the grant was revoked in between.
          setConnectionOverride(
            kind === 'connect' ? 'not-connected' : 'reconnect-required'
          );
        }
      })
      .finally(() => {
        clearTimeout(spinnerTimer);
        setSending(false);
        setShowSpinner(false);
      });
  }, [recipients, subject, body, attachment, fetchService, onClose]);

  const warning = useMemo(() => lossWarning(losses), [losses]);

  const attachmentStatus = useMemo(() => {
    if (exportState === 'running') return 'Preparing attachment…';
    if (exportState === 'failed')
      return 'Could not prepare the attachment — this document failed to export. Nothing has been sent.';
    if (attachment) return `Attaching ${attachmentLabel(attachment)}`;
    return 'No attachment — document is empty';
  }, [exportState, attachment]);

  // Send is only live when we believe a send can actually succeed.
  //
  // `'unknown'` — the status call itself failed — deliberately counts as
  // sendable. The backend is the authority on whether a grant is usable, and
  // `mapSendError` already turns its rejection into the right prompt; letting
  // a transient blip on a *convenience* check lock a user out of the feature
  // would be a worse failure than one wasted round trip.
  //
  // Gated on the export too. Without that, a click during "Preparing
  // attachment…" sent the email with NO attachment and then showed a success
  // toast — a window that is much wider for a doc with images, since the zip
  // path fetches every blob before it generates anything.
  const canSend =
    !sending &&
    exportState === 'done' &&
    (connection === 'connected' || connection === 'unknown');

  return (
    <Modal
      open={open}
      onOpenChange={(next: boolean) => {
        // A send in flight is uninterruptible from the UI: Send/Cancel are
        // disabled while `sending`, but Radix also dismisses on backdrop
        // click and Escape — those aren't buttons, so they bypass a plain
        // `disabled` prop and need to be gated here too. Without this, a
        // user can close the dialog mid-send; the request's eventual
        // failure would then land on an unmounted component and be lost,
        // and "stays open with contents intact on failure" would be
        // meaningless because the dialog is already gone.
        if (sending) return;
        if (!next) onClose();
      }}
      persistent={sending}
      width={480}
      title="Send by email"
    >
      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="send-email-recipients">
            Recipients
          </label>
          <RecipientsInput value={recipients} onChange={setRecipients} />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="send-email-subject">
            Subject
          </label>
          <Input
            id="send-email-subject"
            type="text"
            size="large"
            placeholder="Subject"
            value={subject}
            onChange={setSubject}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="send-email-body">
            Message
          </label>
          <textarea
            id="send-email-body"
            className={styles.textarea}
            placeholder="Write a short note to go with the attachment (optional)"
            value={body}
            onChange={e => setBody(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="send-email-format">
            Format
          </label>
          <select
            id="send-email-format"
            className={styles.select}
            aria-label="Format"
            value={format}
            onChange={handleFormatChange}
          >
            <option value="markdown">Markdown</option>
            <option value="html">HTML</option>
            <option value="docx">Word (.docx)</option>
            <option
              value="pdf"
              disabled
              title="PDF export needs a renderer that doesn't exist yet (YOD-428)"
            >
              PDF (coming soon)
            </option>
          </select>
        </div>

        <div
          aria-live="polite"
          className={clsx(styles.attachmentStatus, {
            // Only the genuine failure reads as an error. "Preparing…",
            // "Attaching X" and "document is empty" are all just information.
            [styles.attachmentStatusError]: exportState === 'failed',
          })}
        >
          {attachmentStatus}
        </div>

        {connection === 'not-connected' && (
          <div role="alert" className={styles.connectPrompt}>
            Connect your Google account to send email through Gmail.
            <Button
              variant="primary"
              onClick={handleGoogleAuthRequired}
              disabled={googleAuthPending}
              loading={showGoogleAuthSpinner}
            >
              Connect Google
            </Button>
          </div>
        )}
        {connection === 'reconnect-required' && (
          <div role="alert" className={styles.connectPrompt}>
            Your Google connection expired or was revoked. Reconnect to send
            this email.
            <Button
              variant="primary"
              onClick={handleGoogleAuthRequired}
              disabled={googleAuthPending}
              loading={showGoogleAuthSpinner}
            >
              Reconnect Google
            </Button>
          </div>
        )}
        {error !== null && (
          <div role="alert" className={styles.errorMessage}>
            {error}
          </div>
        )}

        {/* Informational only — see the module doc on `lossWarning`. It
            never disables Send: the user decides whether the loss matters. */}
        {warning && (
          <div className={styles.lossWarning}>
            {warning.plural
              ? `${warning.count} items couldn't be converted to Word.`
              : `${warning.count} item couldn't be converted to Word.`}
          </div>
        )}

        {/* Once connected, the connect prompt above disappears and nothing else
            tells the user HOW the message goes out. It leaves from their own
            Gmail account and lands in their Sent folder — surprising if
            unstated, and a selling point when stated. It also keeps the
            in-product description consistent with what the OAuth consent screen
            asks for, which is what a verification reviewer compares.

            Deliberately not naming the address: the account's email is not
            stored (ConnectedAccount holds only Google's opaque providerAccountId)
            and cannot be read back, because the grant requests gmail.send and
            the two contacts scopes and NOT openid/email. Naming it would mean
            adding a scope, which changes the consent screen. */}
        {connection === 'connected' && (
          <div className={styles.sendVia}>Sends from your Gmail account.</div>
        )}

        <div className={styles.footer}>
          <Button onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!canSend}
            loading={showSpinner}
          >
            Send
          </Button>
        </div>
      </div>
    </Modal>
  );
};
